import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE, ASC } from "const"
import { MarkovRedistributionStateFrom } from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    MarkovRedistributionStateFromValidation,

    MarkovExpressionConvertToView,
    MarkovExpressionParseElements,
    MarkovExpressionCalc,
    SortMarkovStates,
    SaveMarkovRedistributionStateFrom,
    DeleteMarkovRedistributionStateFrom,

    setMarkovRedistributionStateFromEdit,
    setMarkovRedistributionStateFromReloadList,
    setMarkovRedistributionStateFromReloadEdit,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    SelectDropdown,
    SelectDropdownSearch,
    SelectDropdownItem,
    Feedback,
    Button,
    SpinnerIcon,
    Empty,
} from "client/components"
import ExpressionEditor from "client/pages/markov/expressionEditor"

interface EditRedistributionStateProps {
    redistributionState: MarkovRedistributionStateFrom
}

let isMounted: boolean

const Form = ({
    redistributionState,
}: EditRedistributionStateProps) => {
    const modelStateList = useSelector((state: StoreType) => state.markovState.stateList)
    const variableList = useSelector((state: StoreType) => state.markovVariable.variableList)
    const variableListByName = useSelector((state: StoreType) => state.markovVariable.variableListByName)
    const redistributionSelected = useSelector((state: StoreType) => state.markovRedistribution.redistributionSelected)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [stateId, setStateId] = useState(redistributionState.stateId)
    const [expression, setExpression] = useState("")

    const [stateIds, setStateIds] = useState([])
    const [stateSearch, setStateSearch] = useState("")

    const [validation, setValidation] = useState<MarkovRedistributionStateFromValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        setExpression(MarkovExpressionConvertToView(redistributionState.expression, variableList))

        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const sortedIds = SortMarkovStates(Object.values(modelStateList), "order", ASC, { search: stateSearch })
        setStateIds(sortedIds)
    }, [modelStateList, stateSearch])

    const onClose = () => {
        if (isMounted && modals.length) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        // replace varible names with var_IDs
        let expressionWithVars = ""
        if (expression.trim().length) {
            const parsedExp = MarkovExpressionParseElements(expression.trim())
            parsedExp.forEach((parsed) => {
                if (variableListByName[parsed]) expressionWithVars += `var_${variableListByName[parsed].id}`
                else expressionWithVars += parsed
            })

            // validate and calculate result expression
            const calcResult = MarkovExpressionCalc(null, expressionWithVars, variableList, lang)
            if (calcResult.error) {
                setValidation({ expression: calcResult.error })
                setSaveState(IDLE)
                setApplyState(IDLE)
                return
            }
        }

        Promise.all([
            DeleteMarkovRedistributionStateFrom(redistributionState.stateId, redistributionSelected.id),
            DeleteMarkovRedistributionStateFrom(stateId, redistributionSelected.id),
        ]).then(() => {
            const newRedistributionStateData: MarkovRedistributionStateFrom = {
                redistributionId: redistributionSelected.id,
                stateId,
                expression: expressionWithVars,
            }
            SaveMarkovRedistributionStateFrom(newRedistributionStateData, lang).then((resValidation) => {
                if (isMounted) {
                    setValidation(resValidation)
                    setSaveState(IDLE)
                    setApplyState(IDLE)
                }
                if (resValidation) return

                dispatch(setMarkovRedistributionStateFromEdit(newRedistributionStateData))

                dispatch(setMarkovRedistributionStateFromReloadList(false))
                setTimeout(() => {
                    dispatch(setMarkovRedistributionStateFromReloadList(true))
                }, 0)

                if (mode === 1) onClose()
                if (mode === 2) {
                    dispatch(setMarkovRedistributionStateFromReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setMarkovRedistributionStateFromReloadEdit(true))
                    }, 0)
                }
            })
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.state}</Label>
                        <SelectDropdown title={modelStateList[stateId] ? modelStateList[stateId].name : <Empty />}>
                            <SelectDropdownSearch
                                value={stateSearch}
                                onChange={(e) => setStateSearch(e.target.value)}
                                onClearSearch={() => setStateSearch("")}
                            />
                            {
                                stateIds.map((id) => (
                                    <SelectDropdownItem
                                        key={id}
                                        onClick={() => setStateId(id)}
                                    >
                                        {modelStateList[id] && modelStateList[id].name}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.stateId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.expression}</Label>
                        <ExpressionEditor
                            value={expression}
                            onChange={(val) => setExpression(val)}
                        />
                        <Feedback>{validation && validation.expression}</Feedback>
                    </FormGroup>
                </Col>

                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
